import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Alert, Snackbar, AlertColor, SnackbarCloseReason, Button, Box } from '@mui/material';

export interface NotificationOptions {
  message: string;
  type?: AlertColor;
  duration?: number;
  persistent?: boolean;
  retryable?: boolean; // 是否可重試
  onRetry?: () => void | Promise<void>; // 重試回調
  category?: 'network' | 'api' | 'auth' | 'data' | 'ui' | 'system'; // 錯誤類別
  metadata?: Record<string, any>; // 附加的用來診斷的資料
}

interface Notification extends NotificationOptions {
  id: string;
  timestamp: number;
}

interface NotificationContextType {
  showNotification: (options: NotificationOptions) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, persistent?: boolean) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  hideNotification: (id?: string) => void;
  clearAll: () => void;
  // 智能錯誤處理方法
  showRetryableError: (message: string, onRetry: () => void | Promise<void>, category?: NotificationOptions['category']) => void;
  showNetworkError: (message?: string, onRetry?: () => void | Promise<void>) => void;
  showApiError: (message: string, canRetry?: boolean, onRetry?: () => void | Promise<void>) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const hideNotification = useCallback((id?: string) => {
    if (id) {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } else {
      // Hide the oldest notification if no ID provided
      setNotifications(prev => prev.slice(1));
    }
  }, []);

  const showNotification = useCallback((options: NotificationOptions) => {
    const notification: Notification = {
      id: generateId(),
      timestamp: Date.now(),
      type: 'info',
      duration: 4000,
      persistent: false,
      ...options,
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-hide notification if not persistent
    if (!notification.persistent && notification.duration && notification.duration > 0) {
      setTimeout(() => {
        hideNotification(notification.id);
      }, notification.duration);
    }
  }, [hideNotification]);

  const showSuccess = useCallback((message: string, duration = 4000) => {
    showNotification({ message, type: 'success', duration });
  }, [showNotification]);

  const showError = useCallback((message: string, persistent = false) => {
    showNotification({ 
      message, 
      type: 'error', 
      persistent,
      duration: persistent ? 0 : 6000 
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, duration = 5000) => {
    showNotification({ message, type: 'warning', duration });
  }, [showNotification]);

  const showInfo = useCallback((message: string, duration = 4000) => {
    showNotification({ message, type: 'info', duration });
  }, [showNotification]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // 智能錯誤處理方法實作
  const showRetryableError = useCallback((
    message: string, 
    onRetry: () => void | Promise<void>, 
    category?: NotificationOptions['category']
  ) => {
    showNotification({
      message,
      type: 'error',
      persistent: true,
      retryable: true,
      onRetry,
      category
    });
  }, [showNotification]);

  const showNetworkError = useCallback((
    message = '網路連線失敗，請檢查網路連線', 
    onRetry?: () => void | Promise<void>
  ) => {
    showNotification({
      message,
      type: 'error',
      persistent: true,
      retryable: !!onRetry,
      onRetry,
      category: 'network',
      metadata: {
        networkError: true,
        timestamp: new Date().toISOString()
      }
    });
  }, [showNotification]);

  const showApiError = useCallback((
    message: string, 
    canRetry = false, 
    onRetry?: () => void | Promise<void>
  ) => {
    showNotification({
      message,
      type: 'error',
      persistent: canRetry,
      duration: canRetry ? 0 : 6000,
      retryable: canRetry && !!onRetry,
      onRetry,
      category: 'api',
      metadata: {
        apiError: true,
        canRetry,
        timestamp: new Date().toISOString()
      }
    });
  }, [showNotification]);

  const handleClose = (
    id: string,
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    hideNotification(id);
  };

  // ARCH-05:value 以 useMemo 提供;timeout 記錄以便 provider 卸載時清理
  const contextValue: NotificationContextType = React.useMemo(() => ({
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
    clearAll,
    showRetryableError,
    showNetworkError,
    showApiError,
  }), [
    showNotification, showSuccess, showError, showWarning, showInfo,
    hideNotification, clearAll, showRetryableError, showNetworkError, showApiError,
  ]);

  // Get the topmost notification to display
  const currentNotification = notifications[0];

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {currentNotification && (
        <Snackbar
          key={currentNotification.id}
          open={true}
          autoHideDuration={currentNotification.persistent ? null : (currentNotification.duration || 4000)}
          onClose={(event, reason) => handleClose(currentNotification.id, event, reason)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{
            mt: 8, // Account for header height
          }}
        >
          <Alert
            onClose={() => hideNotification(currentNotification.id)}
            severity={currentNotification.type}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: '320px',
              maxWidth: '600px',
            }}
            action={
              currentNotification.retryable && currentNotification.onRetry ? (
                <Box sx={{ ml: 1 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={async () => {
                      try {
                        if (currentNotification.onRetry) {
                          await currentNotification.onRetry();
                          hideNotification(currentNotification.id);
                        }
                      } catch (error) {
                        console.error('Retry failed:', error);
                        // 可以在這裡新增新的錯誤通知
                      }
                    }}
                    sx={{
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    重試
                  </Button>
                </Box>
              ) : null
            }
          >
            {currentNotification.message}
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};
